import { useEffect, useMemo, useState } from "react";
import { App_Url_New } from "@/src_new/shared/common/static";
import Loader from "@/src_new/shared/components/Loader";
import { usePosterReducers } from "@/src_new/shared/redux/store/useGetData";
import AuthHeader from "@/src_new/shared/secured/components/AuthHeader";
import SideBar from "@/src_new/shared/secured/components/Sidebar";
import { useDispatch } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom"; // Combined imports

const LOVABLE_URL = "https://commercial-bridge-pro.lovable.app";

const MODULE_NAME_BY_PATH_PREFIX: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/home": "Dashboard",
  "/leads": "Leads",
  "/lead": "Lead",
  "/opportunity": "Opportunity",
  "/opportunities": "Opportunities",
  "/pipeline": "Pipeline",
  "/pipeline-analytics": "Pipeline Analytics",
  "/rfqs": "RFQs",
  "/form-config": "Form Config",
  "/commissions": "Commissions",
  "/invoices": "Invoices",
  "/payments": "Payments",
  "/policies": "Policies",
  "/knowledge": "Knowledge",
  "/tax": "Tax",
};

const toTitleCase = (value: string) => value.replace(/\b\w/g, (char) => char.toUpperCase());

const getModuleNameFromPath = (path: string) => {
  const mapped = Object.entries(MODULE_NAME_BY_PATH_PREFIX).find(([prefix]) =>
    path.startsWith(prefix)
  );
  if (mapped) return mapped[1];

  const firstSegment = path.split("/").filter(Boolean)[0] || "Module";
  return toTitleCase(firstSegment.replace(/[-_]+/g, " "));
};

export default function AuthLayout() {
  const {
    auth: { user },
  } = usePosterReducers();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const pathname = useLocation().pathname;

  const isDashboardRoute = pathname === "/dashboard" || pathname === "/home";
  const isLeadsRoute = pathname.startsWith(App_Url_New.secured.frontlinks.leads);
  const isOpportunityRoute =
    pathname.startsWith("/opportunity") ||
    pathname.startsWith("/opportunities") ||
    pathname.startsWith(App_Url_New.secured.frontlinks.opportunitiesAdd);
  const isLeadDetailRoute = /^\/lead\/[^/]+$/.test(pathname);
  const isPipelineAnalyticsRoute = pathname.startsWith("/pipeline-analytics");
  const isRfqRoute = pathname.startsWith("/rfqs");
  const isFormConfigRoute = pathname.startsWith("/form-config");
  const isCommissionsRoute = pathname.startsWith("/commissions");
  const isInvoicesRoute = pathname.startsWith("/invoices");
  const shouldRenderOutlet =
    isLeadsRoute ||
    isLeadDetailRoute ||
    isOpportunityRoute ||
    isDashboardRoute ||
    isPipelineAnalyticsRoute ||
    isRfqRoute ||
    isFormConfigRoute ||
    isCommissionsRoute ||
    isInvoicesRoute;

  const iframeSrc = useMemo(
    () =>
      pathname === "/dashboard"
        ? `${LOVABLE_URL}/?embed=true`
        : `${LOVABLE_URL}${pathname}?embed=true`,
    [pathname]
  );

  const loadingMessage = useMemo(() => `Loading ${getModuleNameFromPath(pathname)}...`, [pathname]);

  const [isIframeLoading, setIsIframeLoading] = useState(!shouldRenderOutlet);

  useEffect(() => {
    setIsIframeLoading(!shouldRenderOutlet);
  }, [shouldRenderOutlet, pathname]);

  return (
    <div className="h-screen flex overflow-hidden">
      <SideBar />

      <main className="flex-1 flex flex-col overflow-hidden">
        <AuthHeader />

        <div className="flex-1 overflow-auto thin-scrollbar m-2">
          {shouldRenderOutlet ? (
            <Outlet />
          ) : (
            <div className="relative h-full w-full">
              {isIframeLoading ? (
                <Loader
                  message={loadingMessage}
                  className="absolute inset-0 z-10 bg-theme-surface"
                />
              ) : null}

              <iframe
                src={iframeSrc}
                title="App"
                onLoad={() => setIsIframeLoading(false)}
                className={`h-full w-full border-none transition-opacity duration-200 ${
                  isIframeLoading ? "opacity-0" : "opacity-100"
                }`}
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
